import { useMediaQuery, useTheme, Theme } from '@mui/material'
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../Redux'
import UIActions from '../Redux/uiRedux'
import {createAppTheme, themeAssets} from './themes'
import {get} from 'lodash'
import {LoginCredentialsType} from './types'
import {ThemeType} from '../interfaces'

export const usePrevious = <T>(value: T): MutableRefObject<T>['current'] => {
    const ref = useRef<T>()
    useEffect(() => {
        ref.current = value
    })
    return ref.current as T
}

type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export const useHistory = () => {
    const navigate = useNavigate();

    return useMemo(() => ({
        push: (url) => navigate(url)
    }), [navigate])
}

export const useAppThemeName = () =>
    useSelector((state: RootState) =>
        state.ui?.options?.themeName ?? (state.ui?.settings?.booking?.enabled === undefined ? 'white' : 'default'))

export const useAppThemeOptions = (): ThemeType => get(themeAssets, useAppThemeName())

export const useAppTheme = (): Theme => {
    const name = useAppThemeName()
    const options = useAppThemeOptions()

    return useMemo(() => {
        return createAppTheme(name, options)
    }, [name, options])
}

export const useSelfServiceThemeName = () =>
    useSelector((state: RootState) =>
        state.ui?.options?.selfService?.themeName ?? 'selfservice')

export const useSelfServiceThemeOptions = (): ThemeType => {
    const themeOptions = get(themeAssets, useSelfServiceThemeName())
    const customThemeOptions = useSelector((state: RootState) =>
        state.ui?.options?.selfService?.themeOptions) ?? {}
    return {selfService: true, ...themeOptions, ...customThemeOptions}
}

export const useSelfServiceTheme = (): Theme => {
    const name = useSelfServiceThemeName()
    const options = useSelfServiceThemeOptions()

    return useMemo(() => {
        return createAppTheme(name, options)
    }, [name, options])
}

export const isSelfService = (): boolean => {
    return window.location.pathname?.startsWith('/self-service')
}
export const useApiKey = (): LoginCredentialsType => {
    const apiKey = isSelfService() ? localStorage.getItem('apiKey') : null;

    return useMemo(() => ({
        apiKey,
        systemName: apiKey ? localStorage.getItem('systemName') : null,
        chainId: apiKey ? localStorage.getItem('chainId') : null,
        stationId: apiKey ? localStorage.getItem('stationId') : null,
    }), [apiKey])
}

const isApiKeySet = (): boolean => {
    return isSelfService() ? !! localStorage.getItem('apiKey') : false
}

export const getApiKeyHeaders = (): {'api-key'?: string | null, 'api-system-name'?: string | null} => {
    return isApiKeySet() ? {
        'api-system-name': localStorage.getItem('systemName'),
        'api-key': localStorage.getItem('apiKey'),
    } : {}
}

export const useMobile = (
    size?: ScreenSize | number,
    options?: { unit: string },
): boolean => {
    const theme = useTheme()
    if (typeof size === 'string') {
        return useMediaQuery(theme.breakpoints.down(size || 'sm'))
    }
    return useMediaQuery(`@media (max-width:${size}${options?.unit})`)
}

export const useScrollPosition = () => {
    const [scrollPosition, setScrollPosition] = useState(0)
    const handleScroll = () => {
        const position = window.pageYOffset
        setScrollPosition(position)
    }

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])
    return scrollPosition
}

export const useSettingsByToken = (token: string, history: any, embedded: boolean) => {
    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(UIActions.getSettingsByToken(token, history, embedded))
    }, [token, dispatch, embedded, history])
}

export const useSettings = (chainIdUrl: string, stationIdUrl: string, history: any, embedded: boolean) => {
    const dispatch = useDispatch()
    useEffect(() => {
        if (stationIdUrl && chainIdUrl) {
            dispatch(UIActions.getSettings(chainIdUrl, stationIdUrl, history, embedded))
        }
        else if (chainIdUrl) {
            dispatch(UIActions.getSettings(chainIdUrl, null, history, embedded))
        }
    }, [chainIdUrl, dispatch, embedded, history, stationIdUrl])
}

export const useTitle = () => {
    const { ui } = useSelector(({ ui }: RootState) => ({
        ui,
    }))
    return ui.settings?.station?.name || ui.settings?.chain?.name || ''
}

type IntervalFunction = () => unknown | void

export const useInterval = (
    callback: IntervalFunction,
    delay: number | null,
) => {
    const savedCallback = useRef<IntervalFunction | null>(null)

    useEffect(() => {
        if (delay === null) return
        savedCallback.current = callback
    })

    useEffect(() => {
        if (delay === null) return
        const tick = () => {
            if (savedCallback.current !== null) {
                savedCallback.current()
            }
        }
        const id = setInterval(tick, delay)
        return () => clearInterval(id)
    }, [delay])
}
